import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import { assetModalParamsToOmit } from '~/components/AssetModalV2/utils';
import {
  createSubSideNavBrowseLastPathSlice,
  type SubSideNavBrowseLastPathSlice,
} from '~/components/v2/store/features/workspace/slices/createSubSideNavBrowseLastPathSlice';
import { ZustandStorageKeys } from '~/constants/zustandStorageKeys';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { isSubSideNavBrowseLastPathname } from '~/utils/isSubSideNavBrowseLastPathname';
import { getBoardHrefFromPrivateCanvasPath, getQueryWithoutParams } from '~/utils/PathUtils';
import { pathnameFromRouterUrl } from '~/utils/subscribeSubSideNavWidthSync';

/** Asset details live at `/c/{clipId}` on the browse route. Libraries restores this path later. */
const PRIVATE_ASSET_MODAL_PATH_SEGMENT = '/c/';

const getBrowsePathWithoutPrivateAssetModal = (path: string) => {
  const pathWithoutHash = path.split('#')[0] ?? path;
  const [pathname, query] = pathWithoutHash.split('?');
  const clipSegmentIndex = pathname.indexOf(PRIVATE_ASSET_MODAL_PATH_SEGMENT);

  if (clipSegmentIndex === -1) {
    return pathWithoutHash;
  }

  const pathnameWithoutClip = pathname.slice(0, clipSegmentIndex);
  const search = getQueryWithoutParams(query ?? '', assetModalParamsToOmit).toString();

  return search ? `${pathnameWithoutClip}?${search}` : pathnameWithoutClip;
};

const SUB_SIDE_NAV_BROWSE_LAST_PATH_STORE_VERSION = 1;

export type SubSideNavBrowseLastPathState = SubSideNavBrowseLastPathSlice;

export const useSubSideNavBrowseLastPathStore = create<SubSideNavBrowseLastPathState>()(
  persist(
    (...args) => ({
      ...createSubSideNavBrowseLastPathSlice({ lastPathByWorkspaceId: {} })(...args),
    }),
    {
      name: ZustandStorageKeys.SubSideNavBrowseLastPath,
      partialize: (state) => ({
        lastPathByWorkspaceId: state.lastPathByWorkspaceId,
      }),
      version: SUB_SIDE_NAV_BROWSE_LAST_PATH_STORE_VERSION,
    },
  ),
);

export const persistSubSideNavBrowseLastPath = ({ path, workspaceId }: { path: string; workspaceId: string }) => {
  const pathWithoutAssetModal = getBrowsePathWithoutPrivateAssetModal(path);
  const pathToStore = getBoardHrefFromPrivateCanvasPath(pathWithoutAssetModal) ?? pathWithoutAssetModal;

  if (!isSubSideNavBrowseLastPathname(pathnameFromRouterUrl(pathToStore))) {
    return;
  }

  useSubSideNavBrowseLastPathStore.getState().setLastPath({ path: pathToStore, workspaceId });
};

type UsePersistSubSideNavBrowseLastPathParams = {
  workspaceId: string | undefined;
};

export const usePersistSubSideNavBrowseLastPath = ({ workspaceId }: UsePersistSubSideNavBrowseLastPathParams) => {
  const { asPath } = useRouter();

  useEffect(() => {
    if (!workspaceId) {
      return;
    }

    persistSubSideNavBrowseLastPath({ path: asPath, workspaceId });
  }, [asPath, workspaceId]);
};

export const useSubSideNavBrowseLastPathHref = (fallbackHref: string) => {
  const { currentWorkspace } = useCurrentWorkspace();
  const workspaceId = currentWorkspace?.id;
  const storedLastPath = useSubSideNavBrowseLastPathStore((state) =>
    workspaceId ? state.lastPathByWorkspaceId[workspaceId] : undefined,
  );
  const lastPath = storedLastPath ? getBrowsePathWithoutPrivateAssetModal(storedLastPath) : undefined;

  if (!lastPath || !isSubSideNavBrowseLastPathname(pathnameFromRouterUrl(lastPath))) {
    return { subSideNavBrowseLastPathHref: fallbackHref };
  }

  return { subSideNavBrowseLastPathHref: lastPath };
};
